import { useEffect, useMemo, useState } from "react";
import type { Area } from "@probetalab/contracts";
import { run } from "../lib/api.js";
import { moveItem, useDragReorder } from "../lib/useDragReorder.js";
import { useToast } from "../lib/toast.js";
import { errorMessage } from "../lib/useQuery.js";
import { Icon } from "./icons.js";
import { Alert, Button, Card, Field, IconButton, Input, Select, Sheet } from "./ui.js";

/*
 * Catálogo → Áreas: the order of the report's sections (pilot request of 2026-11-02, 19:30:
 * "eliminar las áreas del informe o agregar y ordenarlas como queramos"). An Administrador adds,
 * renames, removes («Quitar») and reorders; everyone else sees the numbered list.
 *
 * Reordering is local until «Guardar orden» sends `catalogue.area.reorder` with every active
 * area once. Rows move by dragging the handle (pointer events, so the tablet's stylus and a
 * finger work, not only a mouse) or with the up/down buttons (touch and keyboard).
 */

export { moveItem };

type Dialog = { kind: "add" } | { kind: "rename"; area: Area } | { kind: "remove"; area: Area } | null;

export function AreasEditor({ areas, studyCount, admin, onChanged }: { areas: Area[]; studyCount: (areaId: string) => number; admin: boolean; onChanged: () => void }) {
  const toast = useToast();
  const active = useMemo(() => areas.filter((a) => a.is_active).sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)), [areas]);
  const serverOrder = active.map((a) => a.id).join(",");
  const [order, setOrder] = useState<string[]>(() => active.map((a) => a.id));
  useEffect(() => setOrder(serverOrder ? serverOrder.split(",") : []), [serverOrder]);
  const byId = useMemo(() => new Map(active.map((a) => [a.id, a])), [active]);
  const rows = order.map((id) => byId.get(id)).filter((a): a is Area => Boolean(a));
  const dirty = order.join(",") !== serverOrder;
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);

  // Drag by the handle (stylus, finger, mouse) or step with the arrows: src/lib/useDragReorder.ts.
  const { dragId, rowRef, gripProps, step } = useDragReorder(setOrder);

  async function saveOrder() {
    setBusy(true);
    try {
      await run("catalogue.area.reorder", { area_ids: order });
      toast.success("Orden de las áreas guardado.");
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Card
        title="Orden de las áreas en el informe"
        pad={false}
        actions={
          admin && (
            <span className="row" style={{ gap: 8 }}>
              {dirty && (
                <Button variant="ghost" onClick={() => setOrder(serverOrder.split(","))} disabled={busy}>
                  Descartar
                </Button>
              )}
              {dirty ? (
                <Button variant="primary" icon="check" busy={busy} onClick={() => void saveOrder()}>
                  Guardar orden
                </Button>
              ) : (
                <Button variant="secondary" icon="plus" onClick={() => setDialog({ kind: "add" })}>
                  Agregar área
                </Button>
              )}
            </span>
          )
        }
      >
        {admin && (
          <p className="small muted" style={{ padding: "12px 18px 0" }}>
            Arrastra cada área por la manija o usa las flechas. El orden nuevo se aplica a las solicitudes que se registren después de guardarlo.
          </p>
        )}
        <div role="list" aria-label="Áreas en orden de informe">
          {rows.map((a, i) => (
            <div
              key={a.id}
              role="listitem"
              ref={rowRef(a.id)}
              className={`list-row${dragId === a.id ? " dragging" : ""}`}
            >
              {admin && (
                <span className="drag-handle" aria-hidden="true" title="Arrastrar" {...gripProps(a.id)}>
                  <Icon name="grip" />
                </span>
              )}
              <span className="num" style={{ width: 40, fontSize: "1.25rem", color: "var(--amber)" }}>
                {i + 1}
              </span>
              <span className="grow primary">{a.name}</span>
              <span className="muted small">{studyCount(a.id) === 1 ? "1 estudio" : `${studyCount(a.id)} estudios`}</span>
              {admin && (
                <span className="row" style={{ gap: 2 }}>
                  <IconButton icon="arrowUp" label={`Subir ${a.name}`} disabled={i === 0 || busy} onClick={() => step(a.id, -1)} />
                  <IconButton icon="arrowDown" label={`Bajar ${a.name}`} disabled={i === rows.length - 1 || busy} onClick={() => step(a.id, 1)} />
                  <IconButton icon="pen" label={`Renombrar ${a.name}`} disabled={dirty} onClick={() => setDialog({ kind: "rename", area: a })} />
                  <IconButton icon="trash" label={`Quitar ${a.name}`} disabled={dirty || rows.length === 1} onClick={() => setDialog({ kind: "remove", area: a })} />
                </span>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Sheet open={dialog !== null} onClose={() => setDialog(null)} title={dialog?.kind === "add" ? "Agregar área" : dialog?.kind === "rename" ? `Renombrar ${dialog.area.name}` : dialog ? `Quitar ${dialog.area.name}` : ""}>
        {dialog?.kind === "add" && <AreaNameForm key="add" initial="" onCancel={() => setDialog(null)} onSaved={() => { setDialog(null); onChanged(); }} />}
        {dialog?.kind === "rename" && <AreaNameForm key={dialog.area.id} area={dialog.area} initial={dialog.area.name} onCancel={() => setDialog(null)} onSaved={() => { setDialog(null); onChanged(); }} />}
        {dialog?.kind === "remove" && (
          <RemoveAreaForm
            key={dialog.area.id}
            area={dialog.area}
            studies={studyCount(dialog.area.id)}
            others={rows.filter((r) => r.id !== dialog.area.id)}
            onCancel={() => setDialog(null)}
            onSaved={() => {
              setDialog(null);
              onChanged();
            }}
          />
        )}
      </Sheet>
    </>
  );
}

function AreaNameForm({ area, initial, onSaved, onCancel }: { area?: Area; initial: string; onSaved: () => void; onCancel: () => void }) {
  const toast = useToast();
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  const n = name.trim();

  async function save() {
    if (!n) return;
    setBusy(true);
    try {
      await run("catalogue.area.upsert", { id: area?.id ?? null, name: n, is_active: true, move_studies_to_area_id: null });
      toast.success(area ? `Área renombrada: ${n}.` : `Área agregada al final: ${n}.`);
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Field label="Nombre del área" hint={area ? undefined : "Se agrega al final del informe; después puedes moverla."}>
        <Input aria-label="Nombre del área" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void save()} autoFocus maxLength={120} />
      </Field>
      <div className="row end">
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant="primary" icon="check" busy={busy} disabled={!n || n === initial} onClick={() => void save()}>
          {area ? "Guardar nombre" : "Agregar"}
        </Button>
      </div>
    </div>
  );
}

function RemoveAreaForm({ area, studies, others, onSaved, onCancel }: { area: Area; studies: number; others: Area[]; onSaved: () => void; onCancel: () => void }) {
  const toast = useToast();
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const needsTarget = studies > 0;

  async function remove() {
    setBusy(true);
    try {
      await run("catalogue.area.upsert", { id: area.id, name: area.name, is_active: false, move_studies_to_area_id: needsTarget ? target : null });
      toast.success(`Área quitada del informe: ${area.name}.`);
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      {needsTarget ? (
        <>
          <Alert tone="amber">
            {studies === 1 ? "Un estudio usa" : `${studies} estudios usan`} esta área. Elige a qué área pasan; después la quitamos del informe.
          </Alert>
          <Field label="Pasar los estudios a">
            <Select aria-label="Pasar los estudios a" value={target} onChange={(e) => setTarget(e.target.value)}>
              <option value="">Elige un área…</option>
              {others.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Select>
          </Field>
        </>
      ) : (
        <p>Ningún estudio usa esta área. Deja de aparecer en el catálogo y en el informe.</p>
      )}
      <p className="small muted">Las solicitudes ya registradas conservan su informe como estaba.</p>
      <div className="row end">
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant="danger" icon="trash" busy={busy} disabled={needsTarget && !target} onClick={() => void remove()}>
          Quitar área
        </Button>
      </div>
    </div>
  );
}
